import type { SearchParams } from '@/hooks/useRouterQuery';

/**
 * The campaign page's one "Connection" filter. Each option asks a different question, so
 * picking one replaces the other. Underneath it still drives three URL params, which the
 * server, saved filter defaults and the analytics cards' click-throughs all use:
 * - `connectionAccepted`: this campaign's own connection request (the same events as the
 *   Connection Requests card).
 * - `connectedUserId`: whether the person is a LinkedIn connection of the viewer, or of
 *   anyone on the team, however that happened.
 * - `notConnectedUserId`: the opposite of `connectedUserId` for one user — the person is not
 *   (or not yet) that user's LinkedIn connection.
 */
type ConnectionQuery = Pick<SearchParams, 'connectionAccepted' | 'connectedUserId' | 'notConnectedUserId'>;

export type ConnectionFilterValue = 'accepted' | 'pending' | 'sent' | 'me' | 'not-me' | 'anyone';

export const CONNECTION_FILTER_QUERY_KEYS = ['connectionAccepted', 'connectedUserId', 'notConnectedUserId'] as const;

/** The URL params with the filter off. Spread it to clear the filter whole. */
export const CONNECTION_FILTER_OFF: ConnectionQuery = {
  connectionAccepted: undefined,
  connectedUserId: undefined,
  notConnectedUserId: undefined,
};

export const CONNECTION_FILTER_OPTIONS: { value: ConnectionFilterValue; label: string; description: string }[] = [
  {
    value: 'accepted',
    label: 'Connected via this campaign',
    description: 'This campaign sent the request and they accepted',
  },
  { value: 'pending', label: 'Request pending', description: 'This campaign sent the request, not accepted yet' },
  { value: 'sent', label: 'Request sent by this campaign', description: 'Accepted or still pending' },
  { value: 'me', label: 'Connected to me', description: 'Your LinkedIn connection, however it happened' },
  { value: 'not-me', label: 'Not connected to me', description: 'Not your LinkedIn connection (yet)' },
  {
    value: 'anyone',
    label: 'Connected to anyone on the team',
    description: 'You or any teammate is connected to them',
  },
];

export function isConnectionFilterValue(value: string): value is ConnectionFilterValue {
  return CONNECTION_FILTER_OPTIONS.some((option) => option.value === value);
}

/** Prefix on a teammate's id when the filter is "not connected to them" (see connectionFilterValue). */
export const NOT_CONNECTED_PREFIX = 'not:';

/**
 * The params to send the server: the one question the chip shows, the others cleared. The
 * picker always sets all three, but a stale link can carry several at once, and sending them
 * all would AND contradictory predicates (connected AND not connected) into an empty list
 * nothing explains. Precedence: the campaign's own request, then not-connected, then connected.
 */
export function connectionFilterParams(query: ConnectionQuery): ConnectionQuery {
  if (query.connectionAccepted != null)
    return { ...CONNECTION_FILTER_OFF, connectionAccepted: query.connectionAccepted };
  if (query.notConnectedUserId != null)
    return { ...CONNECTION_FILTER_OFF, notConnectedUserId: query.notConnectedUserId };
  return { ...CONNECTION_FILTER_OFF, connectedUserId: query.connectedUserId };
}

/**
 * The option a query selects, or null when the filter is off. A teammate's user id (from a
 * shared link or a saved default) has no option of its own and comes back as that id, with
 * `not:` in front when the filter is "not connected to them".
 */
export function connectionFilterValue(query: ConnectionQuery, userId: string): string | null {
  const { connectionAccepted, notConnectedUserId, connectedUserId } = connectionFilterParams(query);
  if (connectionAccepted != null) return connectionAccepted;
  if (notConnectedUserId != null) {
    return notConnectedUserId === userId ? 'not-me' : `${NOT_CONNECTED_PREFIX}${notConnectedUserId}`;
  }
  if (connectedUserId == null) return null;
  if (connectedUserId === '__any__') return 'anyone';
  if (connectedUserId === userId) return 'me';
  return connectedUserId;
}

/** The URL params for an option. Always sets all three, so the other questions are cleared. */
export function connectionFilterQuery(value: ConnectionFilterValue, userId: string): ConnectionQuery {
  switch (value) {
    case 'accepted':
    case 'pending':
    case 'sent':
      return { ...CONNECTION_FILTER_OFF, connectionAccepted: value };
    case 'me':
      return { ...CONNECTION_FILTER_OFF, connectedUserId: userId };
    case 'not-me':
      return { ...CONNECTION_FILTER_OFF, notConnectedUserId: userId };
    case 'anyone':
      return { ...CONNECTION_FILTER_OFF, connectedUserId: '__any__' };
  }
}
